import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { trackNavigationClick } from '../lib/analytics';

function NavLink({ to, children, onClick }: { to: string; children: React.ReactNode; onClick?: () => void }) {
  const location = useLocation();
  const isActive = location.pathname === to;
  
  const handleClick = () => {
    // Scroll to top with smooth behavior when clicking home
    if (to === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    trackNavigationClick(children?.toString() || '', to);
    onClick?.();
  };
  
  return (
    <Link
      to={to}
      onClick={handleClick}
      className={`block px-4 py-2.5 text-lg transition-all duration-200 relative ${
        isActive 
          ? 'text-blue-600 font-semibold after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-blue-600 after:rounded-full md:after:-bottom-[1.1rem]' 
          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50/80 md:hover:bg-transparent md:hover:text-blue-600'
      }`}
    >
      {children}
    </Link>
  );
}

function Navigation() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const handleLogoClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setIsOpen(false);
  };

  return (
    <nav className="bg-white py-4 sticky top-0 z-50 border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between">
          <Link 
            to="/" 
            onClick={handleLogoClick}
            className="text-2xl font-bold text-gray-900 hover:text-blue-600 transition-colors duration-200"
          >
            Utlysningslabbet
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-2">
            <div className="flex items-center">
              <NavLink to="/">Hem</NavLink>
              <NavLink to="/progress">Status</NavLink>
              <NavLink to="/demo">Demo</NavLink>
              <NavLink to="/about">Om oss</NavLink>
              <NavLink to="/qa">Frågor och svar</NavLink>
              <Link 
                to="/waitlist"
                className="ml-4 bg-blue-600 text-white px-6 py-2 rounded-xl font-semibold hover:bg-blue-700 transition-colors duration-200 flex items-center"
              >
                Gå med i väntelistan
              </Link>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
            aria-label={isOpen ? 'Stäng meny' : 'Öppna meny'}
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden fixed inset-x-0 top-[73px] bottom-0 bg-white z-40 overflow-y-auto">
            <div className="p-4 space-y-1 border-t border-gray-100">
              <NavLink to="/" onClick={() => setIsOpen(false)}>Hem</NavLink>
              <NavLink to="/progress" onClick={() => setIsOpen(false)}>Status</NavLink>
              <NavLink to="/demo" onClick={() => setIsOpen(false)}>Demo</NavLink>
              <NavLink to="/about" onClick={() => setIsOpen(false)}>Om oss</NavLink>
              <NavLink to="/qa" onClick={() => setIsOpen(false)}>Frågor och svar</NavLink>
              <Link 
                to="/waitlist"
                onClick={() => setIsOpen(false)}
                className="block mt-4 bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors duration-200 text-center"
              >
                Gå med i väntelistan
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}

export default Navigation;